import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../design/Icon';
import { useToast } from '../../components/shared/Overlay';
import { useHierarchyFilter } from '../../hooks/useHierarchyFilter';
import { errorMessage, reportsAPI } from '../../services/reportsApi';
import { boardCommunityOptions, defaultBoardCommunity, lastMonths, monthLabel } from './reportsLogic';

const SECTIONS = [
  { key: 'volume', label: 'Call volume and service levels' },
  { key: 'concerns', label: 'Top concerns' },
  { key: 'sentiment', label: 'Caller sentiment' },
  { key: 'notable', label: 'Notable calls' },
  { key: 'actions', label: 'Open action items' },
];
const ALL_SECTIONS = SECTIONS.map((section) => section.key);

// The worker runs on the page (pdf.js "fake worker"): a separate worker file would bypass the bundler, and the
// one- or two-page board PDF renders quickly on the main thread.
const loadPdfJs = async () => {
  await import('pdfjs-dist/build/pdf.worker.entry');
  return import('pdfjs-dist/build/pdf');
};

/** Draws every page of the PDF to canvases, so the preview renders the same in every browser, headless included. */
function PdfPreview({ blob, title, onError }) {
  const host = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let doc = null;
    (async () => {
      const pdfjs = await loadPdfJs();
      doc = await pdfjs.getDocument({ data: new Uint8Array(await blob.arrayBuffer()), isEvalSupported: false }).promise;
      const target = host.current;
      if (cancelled || !target) return;
      target.replaceChildren();
      for (let number = 1; number <= doc.numPages && !cancelled; number += 1) {
        const page = await doc.getPage(number);
        const scale = (target.clientWidth || 800) / page.getViewport({ scale: 1 }).width;
        const ratio = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: scale * ratio });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        canvas.style.width = '100%';
        canvas.style.display = 'block';
        canvas.dataset.page = String(number);
        target.appendChild(canvas);
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      }
    })().catch((error) => {
      if (!cancelled) onError(error);
    });
    return () => {
      cancelled = true;
      if (doc) doc.destroy();
    };
  }, [blob, onError]);

  return <div ref={host} role="img" aria-label={title} data-testid="board-pdf-preview" style={{ width: '100%', height: '100%', overflow: 'auto' }} />;
}

/**
 * Board summary: community, month, sections and caller names, with the real PDF from GET reports/community-summary
 * (preview=true, so it is not listed as an export) drawn as the preview.
 */
export function BoardTab({ onScheduleMonthly }) {
  const toast = useToast();
  const { communities, filter, terms } = useHierarchyFilter();
  const months = useMemo(() => lastMonths(12), []);
  const [communityId, setCommunityId] = useState('');
  const [picked, setPicked] = useState(false);
  const [month, setMonth] = useState(months[0]);
  const [counts, setCounts] = useState(undefined);
  const sorted = useMemo(() => boardCommunityOptions(communities, counts), [communities, counts]);
  const [includeNames, setIncludeNames] = useState(false);
  const [sections, setSections] = useState(ALL_SECTIONS);
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCounts(undefined);
    reportsAPI.getBoardCommunities(month).then(
      (list) => !cancelled && setCounts(list),
      () => !cancelled && setCounts(null)
    );
    return () => {
      cancelled = true;
    };
  }, [month]);

  useEffect(() => {
    if (picked || counts === undefined || !sorted.length) return;
    setCommunityId(defaultBoardCommunity(sorted, filter.communityIds));
  }, [sorted, filter.communityIds, picked, counts]);

  const sectionParam = sections.length === ALL_SECTIONS.length ? undefined : sections.join(',');

  useEffect(() => {
    if (!communityId || !sections.length) return undefined;
    let cancelled = false;
    setPreview(null);
    setPreviewError(null);
    reportsAPI.communitySummaryBlob({ communityId, month, includeCallerNames: includeNames, sections: sectionParam }).then(
      ({ blob, filename }) => {
        if (!cancelled) setPreview({ blob, filename });
      },
      async (error) => {
        if (!cancelled) setPreviewError(await errorMessage(error, "The board summary couldn't be rendered"));
      }
    );
    return () => {
      cancelled = true;
    };
  }, [communityId, month, includeNames, sectionParam, sections.length]);

  const onPreviewError = useCallback(() => setPreviewError("The board summary couldn't be rendered"), []);

  const toggleSection = (key) =>
    setSections((current) => (current.includes(key) ? current.filter((k) => k !== key) : ALL_SECTIONS.filter((k) => k === key || current.includes(k))));

  const community = sorted.find((c) => c.id === communityId);

  const download = async () => {
    const result = await reportsAPI.communitySummary({ communityId, month, format: 'pdf', includeCallerNames: includeNames, sections: sectionParam });
    toast(result.success ? `Rendered ${result.filename}` : result.error, result.success ? 'checkCircle' : 'alert');
  };

  const scheduleMonthly = () => {
    if (!community) return;
    const name = `${community.name} board packet`;
    onScheduleMonthly({
      name,
      format: 'Pdf',
      cronExpression: '0 7 1 * *',
      newBoardReport: {
        name,
        dataset: 'Calls',
        template: 'CommunityMonthlySummary',
        columns: [],
        filter: { communityIds: [community.id] },
        dateRange: { preset: 'PreviousMonth' },
        isShared: true,
      },
    });
  };

  return (
    <div className="grid">
      <section className="card c-4">
        <div className="card-head">
          <div className="t">Board summary</div>
        </div>
        <div className="card-body stack">
          <div className="field">
            <label className="label" htmlFor="bc">
              {terms.community}
            </label>
            <select
              className="select"
              id="bc"
              value={communityId}
              onChange={(e) => {
                setPicked(true);
                setCommunityId(e.target.value);
              }}
            >
              {sorted.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label className="label" htmlFor="bm">
              Month
            </label>
            <select className="select" id="bm" value={month} onChange={(e) => setMonth(e.target.value)}>
              {months.map((m) => (
                <option key={m} value={m}>
                  {monthLabel(m)}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <span className="label">Sections</span>
            {SECTIONS.map((section) => (
              <label key={section.key} className="check">
                <input type="checkbox" checked={sections.includes(section.key)} onChange={() => toggleSection(section.key)} />
                {section.label}
              </label>
            ))}
          </div>
          <label className="switch">
            <input type="checkbox" checked={includeNames} onChange={(e) => setIncludeNames(e.target.checked)} /> Include caller names
          </label>
          <p className="caption">Off by default. Board packets identify callers by role and lot, not by name.</p>
          <div className="row">
            <button type="button" className="btn btn-primary" id="b-pdf" onClick={download} disabled={!communityId || !sections.length}>
              <Icon name="download" className="ico-sm" />
              Download PDF
            </button>
            <button type="button" className="btn" onClick={scheduleMonthly} disabled={!community}>
              Schedule monthly
            </button>
          </div>
        </div>
      </section>
      <section className="c-8">
        <div className={['paper', preview ? '' : 'is-loading'].filter(Boolean).join(' ')} aria-label="PDF preview" style={{ padding: 0 }}>
          {previewError ? (
            <div className="caption" style={{ padding: 24 }}>
              {previewError}
            </div>
          ) : !sections.length ? (
            <div className="caption" style={{ padding: 24 }}>
              Choose at least one section.
            </div>
          ) : preview ? (
            <PdfPreview blob={preview.blob} title={`Board summary preview, ${community?.name || ''} ${monthLabel(month)}`} onError={onPreviewError} />
          ) : (
            <div className="skel" style={{ flex: 1, margin: 40 }} />
          )}
        </div>
      </section>
    </div>
  );
}
